import type {
  AudioRepository,
  GetAudioUploadResponse
} from "@/features/chat/domain/repositories/audio";

export const MAX_VOICE_MESSAGE_DURATION_MS = 10 * 60 * 1000;
export const MAX_VOICE_MESSAGE_SIZE_BYTES = 25 * 1024 * 1024;

export interface RecorderFormat {
  recorderMimeType: string;
  uploadMimeType: "audio/webm" | "audio/mp4" | "audio/ogg";
  extension: "webm" | "m4a" | "ogg";
}

const RECORDER_FORMATS: RecorderFormat[] = [
  {
    recorderMimeType: "audio/webm;codecs=opus",
    uploadMimeType: "audio/webm",
    extension: "webm"
  },
  {
    recorderMimeType: "audio/mp4",
    uploadMimeType: "audio/mp4",
    extension: "m4a"
  },
  {
    recorderMimeType: "audio/ogg;codecs=opus",
    uploadMimeType: "audio/ogg",
    extension: "ogg"
  }
];

export const selectRecorderFormat = (
  isTypeSupported: (mimeType: string) => boolean
): RecorderFormat | null =>
  RECORDER_FORMATS.find(({ recorderMimeType }) =>
    isTypeSupported(recorderMimeType)
  ) ?? null;

export const createIdempotencyKey = () => {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex
    .slice(6, 8)
    .join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
};

const unwrapResponse = <T>(response: unknown): T => {
  if (
    response &&
    typeof response === "object" &&
    "data" in response &&
    (response as { data?: unknown }).data !== undefined
  ) {
    return (response as { data: T }).data;
  }
  return response as T;
};

const abortableDelay = (durationMs: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException("The operation was aborted", "AbortError"));
      return;
    }
    const handleAbort = () => {
      window.clearTimeout(timeout);
      reject(new DOMException("The operation was aborted", "AbortError"));
    };
    const timeout = window.setTimeout(() => {
      signal?.removeEventListener("abort", handleAbort);
      resolve();
    }, durationMs);
    signal?.addEventListener("abort", handleAbort, { once: true });
  });

export const waitForAudioReady = async ({
  repository,
  mediaId,
  signal,
  timeoutMs = 90_000,
  intervalMs = 1_000,
  now = Date.now
}: {
  repository: AudioRepository;
  mediaId: string;
  signal?: AbortSignal;
  timeoutMs?: number;
  intervalMs?: number;
  now?: () => number;
}): Promise<void> => {
  const startedAt = now();

  while (now() - startedAt < timeoutMs) {
    const response = await repository.getAudioUpload<
      GetAudioUploadResponse | { data: GetAudioUploadResponse }
    >(mediaId, signal);
    const status = unwrapResponse<GetAudioUploadResponse>(response).status;

    if (status === "ready") return;
    if (status === "failed") {
      throw new Error("Audio processing failed");
    }
    await abortableDelay(intervalMs, signal);
  }

  throw new Error("Audio processing timed out");
};

export const unwrapAudioResponse = unwrapResponse;
